import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getContrastTextColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  User,
  Mail,
  Shield,
  Calendar,
  LogOut,
  Key,
  Loader2,
  Eye,
  EyeOff,
  Check,
  Bell
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, logout, isAdmin, updatePreference } = useAuth();
  const navigate = useNavigate();

  // Calendar layout (V1 = current, V2 = new mobile day-sheet roll-up).
  // Default to V1 to keep existing users on the current experience until
  // they explicitly opt into V2.
  const calendarLayout = user?.preferences?.calendar_layout === 'v2' ? 'v2' : 'v1';
  const [savingCalendarPref, setSavingCalendarPref] = useState(false);

  const handleCalendarLayoutToggle = async (toV2) => {
    setSavingCalendarPref(true);
    try {
      await updatePreference('calendar_layout', toV2 ? 'v2' : 'v1');
      toast.success(`Calendar layout switched to ${toV2 ? 'V2' : 'V1'}`);
    } catch (_e) {
      toast.error('Failed to update preference');
    } finally {
      setSavingCalendarPref(false);
    }
  };
  
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!passwordForm.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      
      toast.success('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // ----- Notification preferences -----
  const [notifPrefs, setNotifPrefs] = useState({ in_app: true, email: false });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/notifications/preferences`);
        if (!cancelled) setNotifPrefs({
          in_app: r.data?.in_app !== false,
          email: !!r.data?.email,
        });
      } catch (_e) {/* keep defaults */}
    })();
    return () => { cancelled = true; };
  }, []);

  const updatePref = async (key, value) => {
    const next = { ...notifPrefs, [key]: value };
    setNotifPrefs(next);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/notifications/preferences`, next);
      toast.success('Notification preferences updated');
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
          Profile
        </h1>
        <p className="text-slate-500 mt-1">Manage your account information</p>
      </div>

      {/* Account Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">Account Information</CardTitle>
          <p className="text-sm text-slate-500">Your personal details and account status</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar & Name */}
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold shadow-sm"
              style={{ backgroundColor: user.calendar_color || '#f59e0b', color: getContrastTextColor(user.calendar_color || '#f59e0b') }}
            >
              {user.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{user.full_name}</h2>
              <Badge className="capitalize mt-1">{user.role}</Badge>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Full Name</p>
                <p className="text-slate-900">{user.full_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Email Address</p>
                <p className="text-slate-900">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</p>
                <p className="text-slate-900 capitalize">{user.role}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Member Since</p>
                <p className="text-slate-900">
                  {user.member_since ? format(new Date(user.member_since), 'MMM d, yyyy') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Display Preferences */}
      <Card className="border-slate-200" data-testid="display-prefs-card">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-amber-500" />
            Display Preferences
          </CardTitle>
          <p className="text-sm text-slate-500">
            Tweak how you see things. You can switch back at any time.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center justify-between p-3 rounded-md border border-slate-200 cursor-pointer">
            <div className="min-w-0 pr-3">
              <p className="font-medium text-slate-900">Calendar Layout V2</p>
              <p className="text-xs text-slate-500">
                Tap a day on mobile to see PTO/training/holidays as cards plus a single roll-up tile for that day's jobs. Tap the tile to expand the full job list. Desktop view is unchanged.
              </p>
            </div>
            <Switch
              checked={calendarLayout === 'v2'}
              onCheckedChange={(v) => handleCalendarLayoutToggle(!!v)}
              disabled={savingCalendarPref}
              data-testid="calendar-layout-v2-toggle"
            />
          </label>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="border-slate-200" data-testid="notification-prefs-card">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" />
            Notifications
          </CardTitle>
          <p className="text-sm text-slate-500">
            Choose how you want to be notified when items need your attention (e.g. a Field Report or Maintenance form is submitted for your approval).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center justify-between p-3 rounded-md border border-slate-200 cursor-pointer">
            <div>
              <p className="font-medium text-slate-900">In-app notifications</p>
              <p className="text-xs text-slate-500">Show alerts on the dashboard bell.</p>
            </div>
            <Switch
              checked={notifPrefs.in_app}
              onCheckedChange={(v) => updatePref('in_app', v)}
              disabled={savingPrefs}
              data-testid="notif-toggle-in-app"
            />
          </label>
          <label className="flex items-center justify-between p-3 rounded-md border border-slate-200 cursor-pointer">
            <div>
              <p className="font-medium text-slate-900">Email notifications</p>
              <p className="text-xs text-slate-500">Also send me an email at {user.email}.</p>
            </div>
            <Switch
              checked={notifPrefs.email}
              onCheckedChange={(v) => updatePref('email', v)}
              disabled={savingPrefs}
              data-testid="notif-toggle-email"
            />
          </label>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Security</CardTitle>
              <p className="text-sm text-slate-500">Manage your password</p>
            </div>
            {!showPasswordSection && (
              <Button 
                variant="outline"
                onClick={() => setShowPasswordSection(true)}
                data-testid="change-password-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            )}
          </div>
        </CardHeader>
        
        {showPasswordSection && (
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Password Requirements:</strong> Minimum 6 characters. Choose a strong password that you don't use elsewhere.
              </p>
            </div>
            
            <div className="space-y-4">
              {/* Current Password */}
              <div className="space-y-2">
                <Label>Current Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Enter your current password"
                    className="bg-slate-50 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* New Password */}
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter your new password"
                    className="bg-slate-50 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordForm.newPassword && passwordForm.newPassword.length < 6 && (
                  <p className="text-xs text-red-500">Password must be at least 6 characters</p>
                )}
                {passwordForm.newPassword && passwordForm.newPassword.length >= 6 && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Password length OK
                  </p>
                )}
              </div>
              
              {/* Confirm New Password */}
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm your new password"
                    className="bg-slate-50 pr-10"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                )}
                {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Passwords match
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPasswordSection(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="save-password-btn"
              >
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Password
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Admin Access Info */}
      {isAdmin() && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-900">Administrator Access</p>
                <p className="text-sm text-emerald-700">
                  You have full access to all features and settings in the application.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sign Out */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">Sign Out</CardTitle>
          <p className="text-sm text-slate-500">Sign out of your account on this device</p>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
